import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Envelope, GraduationCap, MapPin } from "@phosphor-icons/react";
import axios from "axios";
import { DEFAULT_MEMBERS } from "@/lib/mockData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

export default function EditorialBoardPage() {
  const [members, setMembers] = useState(DEFAULT_MEMBERS);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const response = await axios.get(`${API}/editorial-board`);
        if (Array.isArray(response.data) && response.data.length > 0) {
          setMembers(response.data);
        } else {
          setMembers(DEFAULT_MEMBERS);
        }
      } catch (error) {
        console.error("Failed to fetch editorial board:", error);
        setMembers(DEFAULT_MEMBERS);
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, []);

  return (
    <div data-testid="editorial-board-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-[#0F172A] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0D9488]/30 to-[#1E40AF]/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-[#0D9488] text-xs tracking-[0.2em] uppercase font-bold">
              Our Team
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Chivo'] tracking-tight leading-none mt-4 mb-6">
              Editorial Board
            </h1>
            <p className="text-lg text-[#CBD5E1] leading-relaxed">
              Meet our distinguished team of editors and reviewers who uphold 
              the highest standards of scientific excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Editorial Board Grid */}
      <section data-testid="board-members-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#0D9488] text-xs tracking-[0.2em] uppercase font-bold">
              Expert Guidance
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] font-['Chivo'] tracking-tight mt-4">
              Distinguished Scholars & Researchers
            </h2>
            <p className="text-[#64748B] mt-4 max-w-2xl mx-auto">
              Our editorial board comprises leading experts from prestigious institutions worldwide, 
              ensuring rigorous peer review and scientific integrity.
            </p>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-[#E2E8F0] h-64 rounded-sm mb-4" />
                  <div className="bg-[#E2E8F0] h-6 rounded-sm mb-2 w-3/4" />
                  <div className="bg-[#E2E8F0] h-4 rounded-sm w-full" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {members.map((member, index) => (
                <motion.article
                  key={member.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  data-testid={`board-member-${member.id}`}
                  className="group bg-white border border-[#E2E8F0] rounded-sm overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={member.image_url}
                      alt={member.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <span className="inline-block bg-[#0D9488] text-white text-xs font-bold px-3 py-1 rounded-sm mb-2">
                        {member.title}
                      </span>
                      <h3 className="text-xl font-bold text-white font-['Chivo']">
                        {member.name}
                      </h3>
                    </div>
                  </div>
                  <div className="p-6 space-y-3">
                    <div className="flex items-center gap-2 text-sm text-[#64748B]">
                      <GraduationCap size={18} className="text-[#0D9488]" weight="duotone" />
                      <span>{member.specialty}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#64748B]">
                      <MapPin size={18} className="text-[#0D9488]" weight="duotone" />
                      <span>{member.institution}, {member.country}</span>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Join Section */}
      <section data-testid="join-section" className="py-20 md:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-[#0D9488] text-xs tracking-[0.2em] uppercase font-bold">
                Join Our Team
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] font-['Chivo'] tracking-tight mt-4 mb-4">
                Become a Reviewer
              </h2>
              <p className="text-[#64748B] mb-8">
                We are always looking for qualified researchers and academics to join our peer review network. 
                If you are interested in contributing to scientific advancement, we would love to hear from you.
              </p>
              <Link
                to="/contact"
                data-testid="become-reviewer-btn"
                className="inline-flex items-center gap-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white px-6 py-3 rounded-sm font-medium transition-colors"
              >
                <Envelope size={20} weight="bold" />
                Contact Us to Apply
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-24 bg-gradient-to-r from-[#0D9488] to-[#1E40AF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Chivo'] tracking-tight mb-4">
              Ready to Submit Your Work?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Our editorial team is ready to review your manuscript and guide you through the publication process.
            </p>
            <Link
              to="/submit-manuscript"
              data-testid="editorial-cta-submit"
              className="inline-flex items-center gap-2 bg-white text-[#0D9488] hover:bg-[#F0FDFA] px-8 py-4 rounded-sm font-semibold transition-colors"
            >
              Submit Your Manuscript
              <ArrowRight size={20} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
